import { useEffect, lazy, Suspense } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { AppLayout } from "./layouts/AppLayout";
import { BookingPortal } from "./pages/BookingPortal";
import { LoadingScreen } from "./components/common/LoadingScreen";
import { supabase } from "./lib/supabase";
import { useUI } from "./hooks/useUI";

/**
 * Route-level code splitting.
 *
 * AppLayout and BookingPortal stay eager: they are the storefront hot path a
 * client lands on from a WhatsApp link, and splitting them would only add a
 * request waterfall to the most-visited page.
 *
 * Everything else is lazy. The staff dashboard, SuperAdmin, and WhatsApp
 * management are the heaviest modules in the app and are never opened by a
 * client booking an appointment -- previously they all shipped in the same
 * 1.3MB bundle served to every phone.
 */
const SuperAdmin = lazy(() =>
  import("./pages/SuperAdmin").then((m) => ({ default: m.SuperAdmin })),
);
const WhatsAppManagement = lazy(() =>
  import("./pages/WhatsAppManagement").then((m) => ({
    default: m.WhatsAppManagement,
  })),
);
const BusinessDashboard = lazy(() =>
  import("./pages/BusinessDashboard").then((m) => ({
    default: m.BusinessDashboard,
  })),
);
const UnifiedLogin = lazy(() =>
  import("./pages/UnifiedLogin").then((m) => ({ default: m.UnifiedLogin })),
);
const LandingPage = lazy(() =>
  import("./pages/LandingPage").then((m) => ({ default: m.LandingPage })),
);
const BookingManager = lazy(() =>
  import("./pages/BookingManager").then((m) => ({ default: m.BookingManager })),
);
const WaitlistClaim = lazy(() =>
  import("./pages/WaitlistClaim").then((m) => ({ default: m.WaitlistClaim })),
);
const LeaveReview = lazy(() =>
  import("./pages/LeaveReview").then((m) => ({ default: m.LeaveReview })),
);
const NotFound = lazy(() =>
  import("./pages/NotFound").then((m) => ({ default: m.NotFound })),
);

/**
 * App.tsx (V2.2.0)
 * Purpose: Global routing layer & Auth Monitoring for the Hajez Sovereign platform.
 * Forensic Standard: 0-Leaked State.
 * Protocol: Path-to-Node Resolution | Reactive Security Boundary.
 */
function App() {
  const navigate = useNavigate();
  const location = useLocation();
  const { globalToast } = useUI();

  useEffect(() => {
    let initialEvent = true;

    // Reactive Security Handshake: Listen for background session expiry
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      // Protocol: Ignore the very first SIGNED_OUT event if it's just the client hydrating.
      // AppLayout.tsx handles the heavy lifting of the initial mount-time auth check.
      // App.tsx focus is on reactive background transitions.
      if (event === "SIGNED_OUT" && !initialEvent) {
        // Redirection Scope: Only trigger for internal administrative pages
        const protectedPatterns = ["/super-admin", "/admin"];
        const isProtected = protectedPatterns.some((p) =>
          location.pathname.includes(p),
        );

        if (isProtected) {
          console.warn(
            "[Security Boundary] Reactive session loss detected. Redirecting.",
          );
          globalToast({
            message: "Session Expired. Please log in again.",
            type: "warning",
          });
          navigate("/login?error=session_expired", { replace: true });
        }
      }
      initialEvent = false;
    });

    return () => subscription.unsubscribe();
  }, [navigate, location.pathname, globalToast]);

  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Super Admin Route */}
        <Route path="/super-admin" element={<SuperAdmin />} />
        <Route path="/super-admin/whatsapp" element={<WhatsAppManagement />} />

        {/* Swiss Minimalist Public Landing Page */}
        <Route path="/" element={<LandingPage />} />

        {/* Unified Login Portal - Smart redirect based on role */}
        <Route path="/login" element={<UnifiedLogin />} />

        {/* Dynamic Business Routes */}
        <Route path="/:slug" element={<AppLayout />}>
          {/* Customer Face: Booking & Portal */}
          <Route index element={<BookingPortal />} />

          {/* Direct Booking Link (Specific Slot Management) */}
          <Route path="booking/:id" element={<BookingManager />} />

          {/* Booking Identification Hub (Manage My Booking) */}
          <Route path="manage" element={<BookingManager />} />
          <Route path="manage/:id" element={<BookingManager />} />

          {/* Waitlist Claim Link (from waitlist_slot_offered WhatsApp message) */}
          <Route path="waitlist/:waitlistId" element={<WaitlistClaim />} />

          {/* Review Submission Link (from review_request WhatsApp message) */}
          <Route path="review/:id" element={<LeaveReview />} />

          {/* Admin Face: Dashboard */}
          <Route path="admin" element={<BusinessDashboard />} />
        </Route>

        {/* 404 Fallback */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

export default App;
